import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { UserRole } from '../types';
import {
    LayoutDashboard, Calendar, Folder,
    Users, Briefcase, DollarSign, Settings, Building2,
    ChevronLeft, ChevronRight, ChevronDown,
    PieChart, FileSpreadsheet, MapPin, ClipboardList, Shield, HardDrive, Receipt, CheckSquare, PenLine,
} from 'lucide-react';
import { useAuth } from "../contexts/AuthContext";
import { useSidebar } from "../contexts/SidebarContext";
import { ApprovazioniService, RelazioneGiornalieraService, ServizioFiduciarioService } from '../services/apiService';
import { formatRole } from '../utils/utils';
import { subscribeApprovazioniChanged } from '../utils/approvazioniEvents';

const Sidebar: React.FC = () => {
    const { user, currentSedeId, selectSede, sedi, sediGestiteIds } = useAuth();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const location = useLocation();
    const { isOpen: drawerOpen, close: closeDrawer } = useSidebar();

    // Chiudi drawer mobile/tablet automaticamente al cambio rotta.
    useEffect(() => {
        closeDrawer();
    }, [location.pathname, closeDrawer]);

    // Negli editor relazione la sidebar si chiude da sola per lasciare spazio alla pagina A4.
    useEffect(() => {
        if (location.pathname.startsWith('/report-editor') || location.pathname.startsWith('/relazione-docx')) {
            setIsCollapsed(true);
        }
    }, [location.pathname]);
    // Totale media + lavori + spese per il badge "Approvazioni".
    const [approvazioniTotale, setApprovazioniTotale] = useState(0);
    // Relazioni DA_MODIFICARE del collaboratore loggato (badge "Miei Incarichi").
    const [daModificareCount, setDaModificareCount] = useState(0);
    // Turni fiduciari ASSEGNATO del collaboratore loggato (badge agenda).
    const [turniDaAccettareCount, setTurniDaAccettareCount] = useState(0);

    const toggleSidebar = () => setIsCollapsed(!isCollapsed);
    const isAdmin = user?.role === UserRole.ADMIN;
    const isResponsabile = user?.role === UserRole.RESPONSABILE;
    const isCollaboratore = user?.role === UserRole.COLLABORATORE;

    // Badge "Approvazioni": polling ogni 60s e ricarica immediata su
    // `approvazioni:changed`. Gli errori vengono ignorati.
    useEffect(() => {
        if (!isAdmin && !isResponsabile) return;
        const fetchCount = async () => {
            try {
                const sedeId = typeof currentSedeId === 'number' ? currentSedeId : undefined;
                const summary = await ApprovazioniService.getSummary(sedeId).catch(() => null);
                if (summary) setApprovazioniTotale(summary.totale);
            } catch {
                // silently ignore
            }
        };
        fetchCount();
        const interval = setInterval(fetchCount, 60_000);
        const unsub = subscribeApprovazioniChanged(fetchCount);
        return () => { clearInterval(interval); unsub(); };
    }, [isAdmin, isResponsabile, currentSedeId]);

    // Badge "report da rifare" del collaboratore: polling ogni 60s, errori ignorati.
    useEffect(() => {
        if (!isCollaboratore) return;
        const fetchDaModificare = async () => {
            try {
                const list = await RelazioneGiornalieraService.getMieDaModificare().catch(() => []);
                setDaModificareCount(Array.isArray(list) ? list.length : 0);
            } catch {
                // silently ignore
            }
        };
        fetchDaModificare();
        const interval = setInterval(fetchDaModificare, 60_000);
        return () => clearInterval(interval);
    }, [isCollaboratore]);

    // Turni fiduciari da accettare: polling ogni 60s.
    useEffect(() => {
        if (!isCollaboratore) return;
        const fetchDaAccettare = async () => {
            try {
                const res: any = await ServizioFiduciarioService.getMieiTurniDaAccettare();
                const list = res?.data ?? (Array.isArray(res) ? res : []);
                setTurniDaAccettareCount(Array.isArray(list) ? list.length : 0);
            } catch {
                // silently ignore
            }
        };
        fetchDaAccettare();
        const interval = setInterval(fetchDaAccettare, 60_000);
        return () => clearInterval(interval);
    }, [isCollaboratore]);

    // --- Voce di menu ---
    const NavItem = ({ to, icon, label, indent = false, end = false, badge = 0 }: {
        to: string,
        icon: React.ReactNode,
        label: string,
        indent?: boolean,
        end?: boolean,
        badge?: number
    }) => (
        <NavLink
            to={to}
            end={end}
            title={isCollapsed ? label : undefined}
            className={({ isActive }) => `
            relative flex items-center py-2.5 px-3 my-0.5 rounded-xl font-medium transition-all duration-200 group
            ${isCollapsed ? 'justify-center' : indent ? 'pl-10 space-x-3' : 'space-x-3'}
            ${isActive
                ? 'bg-brand-700/10 text-brand-300 shadow-sm'
                : 'text-slate-400 hover:bg-navy-800 hover:text-slate-200'
            }
        `}
        >
            {({ isActive }) => (
                <>
                    {isActive && !isCollapsed && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-brand-500 rounded-r-full" />
                    )}
                    <span className={`shrink-0 transition-transform duration-200 ${isActive ? 'text-brand-400' : ''} ${!isCollapsed && isActive ? 'scale-110' : ''}`}>
                        {icon}
                    </span>
                    {!isCollapsed && (
                        <span className="truncate text-sm flex-1">{label}</span>
                    )}
                    {badge > 0 && (
                        <span className={`text-[10px] font-black bg-amber-500 text-white rounded-full flex items-center justify-center shrink-0 ${isCollapsed ? 'w-4 h-4 absolute top-1 right-1' : 'w-5 h-5'}`}>
                            {badge > 9 ? '9+' : badge}
                        </span>
                    )}
                </>
            )}
        </NavLink>
    );

    // --- Gruppo di voci ---
    const NavGroup = ({ icon, label, children, activePathPrefix }: { icon: React.ReactNode, label: string, children: React.ReactNode, activePathPrefix?: string[] }) => {
        const isActiveGroup = activePathPrefix ? activePathPrefix.some(path => location.pathname.startsWith(path)) : false;
        const [isOpen, setIsOpen] = useState(isActiveGroup);

        return (
            <div className="my-0.5">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`
                        w-full flex items-center justify-between py-2.5 px-3 rounded-xl font-medium transition-all duration-200
                        ${isActiveGroup ? 'text-slate-200 bg-navy-800/60' : 'text-slate-400 hover:bg-navy-800 hover:text-slate-200'}
                    `}
                >
                    <div className="flex items-center space-x-3">
                        <span className={isActiveGroup ? 'text-brand-400' : ''}>{icon}</span>
                        {!isCollapsed && <span className="text-sm">{label}</span>}
                    </div>
                    {!isCollapsed && <ChevronDown size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-slate-300' : 'text-slate-600'}`}/>}
                </button>
                {!isCollapsed && (
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
                        <div className="flex flex-col gap-0.5">{children}</div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`flex flex-col h-screen shrink-0 z-40 transition-transform lg:transition-all duration-300 ease-out bg-navy-900 border-r border-navy-800 text-slate-200 fixed inset-y-0 left-0 lg:static ${drawerOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} ${isCollapsed ? 'lg:w-20 w-72' : 'w-72'}`}>

            {/* Header */}
            <div className="h-16 flex items-center justify-between px-4 border-b border-navy-800 shrink-0 relative">
                <div className={`flex items-center overflow-hidden ${isCollapsed ? 'justify-center w-full' : ''}`}>

                    {isCollapsed ? (
                        <div className="w-8 h-8 bg-gradient-to-tr from-brand-700 to-brand-500 rounded-lg flex items-center justify-center text-white shadow-lg shrink-0">
                            <span className="font-bold text-sm">UD</span>
                        </div>
                    ) : (
                        <img
                            src="/logo.webp"
                            alt="Ultra Detective Logo"
                            className="h-16 w-auto object-contain"
                        />
                    )}

                </div>

                <button onClick={toggleSidebar} className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-navy-800 border border-navy-700 text-slate-400 rounded-full items-center justify-center hover:text-slate-100 hover:border-brand-500/40 shadow-sm transition-colors z-50">
                    {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                </button>
            </div>

            {/* Selettore sede */}
            {!isCollapsed && (isAdmin || isResponsabile) && (
                <div className="px-4 py-4 animate-in fade-in slide-in-from-left-4 duration-500">
                    <label className="text-[10px] uppercase font-bold text-slate-500 mb-2 block px-1 tracking-wider">Sede Operativa</label>
                    <div className="relative group">
                        {isAdmin ? (
                            <>
                                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-400 pointer-events-none">
                                    <Building2 size={16} />
                                </div>
                                <select
                                    className="w-full bg-navy-800 border border-navy-700 text-slate-200 text-sm rounded-xl py-2.5 pl-10 pr-8 appearance-none outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600/50 transition-all cursor-pointer"
                                    value={currentSedeId || 'ALL'}
                                    onChange={(e) => selectSede(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))}
                                >
                                    <option value="ALL">Tutte le Sedi</option>
                                    {sedi.map(sede => (
                                        <option key={sede.id} value={sede.id} className="bg-navy-800 text-slate-300">
                                            {sede.name}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none group-hover:text-slate-300 transition-colors">
                                    <ChevronDown size={14} />
                                </div>
                            </>
                        ) : (() => {
                            // Solo le sedi gestite: nelle altre il responsabile lavora come
                            // collaboratore e il suo lavoro è in "Miei Incarichi".
                            const sedeIds: Array<number | string> = sediGestiteIds;
                            const activeId = (typeof currentSedeId === 'number')
                                ? currentSedeId
                                : sedeIds[0];
                            const lookupName = (id: any) => {
                                if (!id) return null;
                                const s = sedi.find(x => String(x.id) === String(id));
                                return s?.name || `Sede #${id}`;
                            };
                            // Nessuna sede: avviso esplicito.
                            if (sedeIds.length === 0) {
                                return (
                                    <div className="flex items-center gap-3 bg-rose-900/30 border border-rose-700/40 p-3 rounded-xl text-rose-200">
                                        <div className="p-1.5 bg-rose-700/20 text-rose-400 rounded-lg"><MapPin size={16}/></div>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] text-rose-300 font-medium leading-none mb-1">Sede gestita</span>
                                            <span className="text-sm font-bold text-rose-100 leading-none">Nessuna sede</span>
                                        </div>
                                    </div>
                                );
                            }
                            // Una sola sede: sola lettura.
                            if (sedeIds.length <= 1) {
                                return (
                                    <div className="flex items-center gap-3 bg-navy-800/50 border border-navy-700/50 p-3 rounded-xl text-slate-300">
                                        <div className="p-1.5 bg-brand-700/10 text-brand-400 rounded-lg"><MapPin size={16}/></div>
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-xs text-slate-500 font-medium leading-none mb-1">Sede gestita</span>
                                            <span className="text-sm font-bold text-slate-200 leading-none truncate">
                                                {lookupName(activeId) || 'Sede Operativa'}
                                            </span>
                                        </div>
                                    </div>
                                );
                            }
                            // Più sedi: picker limitato alle sue, senza "Tutte le Sedi".
                            return (
                                <>
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-400 pointer-events-none">
                                        <Building2 size={16} />
                                    </div>
                                    <select
                                        className="w-full bg-navy-800 border border-navy-700 text-slate-200 text-sm rounded-xl py-2.5 pl-10 pr-8 appearance-none outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600/50 transition-all cursor-pointer"
                                        value={activeId ?? ''}
                                        onChange={(e) => selectSede(Number(e.target.value))}
                                    >
                                        {sedeIds.map((sid: any) => {
                                            const s = sedi.find(x => String(x.id) === String(sid));
                                            return (
                                                <option key={sid} value={sid} className="bg-navy-800 text-slate-300">
                                                    {s?.name || `Sede #${sid}`}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none group-hover:text-slate-300 transition-colors">
                                        <ChevronDown size={14} />
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Menu di navigazione */}
            <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5 scrollbar-thin scrollbar-thumb-navy-700">

                {/* Dashboard visibile a tutti: il contenuto si adatta al ruolo. */}
                <NavItem to="/dashboard" icon={<LayoutDashboard size={20} />} label="Dashboard" />
                <NavItem to="/incarichi" icon={<ClipboardList size={20} />} label="Miei Incarichi" badge={isCollaboratore ? (daModificareCount + turniDaAccettareCount) : 0} />
                {/* Spese, km e riepilogo ore: per titolare e responsabile c'è il pulsante in /incarichi. */}
                {isCollaboratore && <NavItem to="/le-mie-spese" icon={<Receipt size={20} />} label="Spese e Ore" />}
                {(!isCollaboratore) && <NavItem to="/calendario" icon={<Calendar size={20} />} label="Calendario" />}

                {!isCollaboratore && (
                    <>
                        <NavItem to="/pratiche" icon={<Folder size={18} />} label="Servizi investigativi" />
                        <NavItem to="/servizi" icon={<Shield size={18} />} label="Servizi fiduciari" />
                        <NavItem to="/preventivi" icon={<FileSpreadsheet size={18} />} label="Preventivi/Fatture Proforma" />
                        {(isAdmin || isResponsabile) && (
                            <NavItem to="/file-manager" icon={<HardDrive size={18} />} label="File Manager" />
                        )}
                    </>
                )}

                <div className="my-2 border-t border-navy-800/60 mx-2"/>

                {(isAdmin || isResponsabile) && (
                    <NavItem to="/firma-digitale" icon={<PenLine size={18} />} label="Firma Digitale" />
                )}
                {/* Firma Digitale (v2) via API: il fornitore blocca la pagina in iframe. */}
                {(isAdmin || isResponsabile) && (
                    <NavItem to="/testotp" icon={<PenLine size={18} />} label="Firma Digitale (v2)" />
                )}

                {/* Hub approvazioni (media, lavori e spese) con badge totale. */}
                {(isAdmin || isResponsabile) && (
                    <NavLink
                        to="/approvazioni"
                        title={isCollapsed ? 'Approvazioni' : undefined}
                        className={({ isActive }) => `
                            relative flex items-center py-2.5 px-3 my-0.5 rounded-xl font-medium transition-all duration-200 group
                            ${isCollapsed ? 'justify-center' : 'space-x-3'}
                            ${isActive
                                ? 'bg-brand-700/10 text-brand-300 shadow-sm'
                                : 'text-slate-400 hover:bg-navy-800 hover:text-slate-200'
                            }
                        `}
                    >
                        {({ isActive }) => (
                            <>
                                {isActive && !isCollapsed && (
                                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-brand-500 rounded-r-full" />
                                )}
                                <span className={`shrink-0 transition-transform duration-200 ${isActive ? 'text-brand-400' : ''} ${!isCollapsed && isActive ? 'scale-110' : ''}`}>
                                    <CheckSquare size={18} />
                                </span>
                                {!isCollapsed && (
                                    <span className="truncate text-sm flex-1">Approvazioni</span>
                                )}
                                {approvazioniTotale > 0 && (
                                    <span className={`text-[10px] font-black bg-rose-600 text-white rounded-full flex items-center justify-center shrink-0 ${isCollapsed ? 'w-4 h-4 absolute top-1 right-1' : 'w-5 h-5'}`}>
                                        {approvazioniTotale > 9 ? '9+' : approvazioniTotale}
                                    </span>
                                )}
                            </>
                        )}
                    </NavLink>
                )}

                {/* /pending-approvals non ha voce in menu: resta raggiungibile da link diretti. */}

                {(isAdmin || isResponsabile) && (
                    <NavGroup icon={<Briefcase size={20} />} label="Gestione Azienda" activePathPrefix={['/clienti', '/sedi', '/utenti', '/amministrazione', '/impostazioni']}>
                        {(isAdmin || isResponsabile) && (
                            <NavItem to="/utenti" icon={<Users size={18} />} label="Collaboratori" indent />
                        )}
                        <NavItem to="/clienti" icon={<Users size={18} />} label="Clienti" indent />
                        {isAdmin && (
                            <NavItem to="/sedi" icon={<Building2 size={18} />} label="Sedi Operative" indent />
                        )}
                        {isAdmin && (
                            <NavItem to="/impostazioni" icon={<Settings size={18} />} label="Impostazioni" indent />
                        )}
                        <div className="mx-3 my-1 border-t border-navy-700/40" />
                        {/* Contabilità visibile anche al responsabile, limitata alle sue sedi. */}
                        {(isAdmin || isResponsabile) && (
                            <NavItem to="/amministrazione" icon={<DollarSign size={18} />} label="Contabilità" indent end={true} />
                        )}
                    </NavGroup>
                )}
            </nav>

            {/* Profilo utente */}
            <div className="border-t border-navy-800 p-4 bg-navy-950/40">
                <div className={`flex items-center gap-3 transition-all ${isCollapsed ? 'justify-center' : ''}`}>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-700 to-brand-900 flex items-center justify-center text-white text-sm font-bold shadow-md shrink-0 cursor-pointer hover:ring-2 ring-brand-500 ring-offset-2 ring-offset-navy-900 transition-all">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    {!isCollapsed && (
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-100 truncate">{user?.name || 'Utente'}</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider">{formatRole(user?.role)}</div>
                        </div>
                    )}
                    {!isCollapsed && (
                        <NavLink to="/profilo" className="text-slate-600 hover:text-brand-400 transition-colors">
                            <Settings size={18} />
                        </NavLink>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
